import type { CSSProperties, ReactNode } from "react";
import { computeDssStyles, type DssStyles } from "@ease/page-builder";

import { buildScopedClassName } from "@/components/pages/style-state-system";

type RenderFn = (props: Record<string, unknown>) => ReactNode;

type PuckComponentDefinition = {
  render?: RenderFn;
} & Record<string, unknown>;

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function isDssStyles(value: unknown): value is DssStyles {
  return isRecord(value);
}

function filterWrapperAttrs(wrapperAttrs: Record<string, string>): Record<string, string> {
  return Object.fromEntries(
    Object.entries(wrapperAttrs).filter(([key]) => key.startsWith("data-") || key.startsWith("aria-") || key === "role"),
  );
}

export function withDssWrapper(renderFn: RenderFn): RenderFn {
  return (props: Record<string, unknown>) => {
    if (!isDssStyles(props._dssStyles)) {
      return renderFn(props);
    }

    const componentId = typeof props.id === "string" && props.id
      ? props.id
      : "puck-component";
    const scopedClass = buildScopedClassName(componentId);
    const computed = computeDssStyles(props._dssStyles, { scopedClass });
    const wrapperAttrs = filterWrapperAttrs(computed.wrapperAttrs);

    return (
      <div
        className={computed.scopedClass}
        style={computed.inlineStyles as CSSProperties}
        {...wrapperAttrs}
      >
        {renderFn(props)}
      </div>
    );
  };
}

export function wrapAllComponents<TConfig extends { components: Record<string, PuckComponentDefinition> }>(
  config: TConfig,
): TConfig {
  return {
    ...config,
    components: Object.fromEntries(
      Object.entries(config.components).map(([name, definition]) => [
        name,
        typeof definition.render === "function"
          ? {
              ...definition,
              render: withDssWrapper(definition.render),
            }
          : definition,
      ]),
    ),
  } as TConfig;
}
